import type { MouseEvent } from 'react';
import { Fragment } from 'react';

import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';
import { StepIcon } from '@poseidon/components/StepIcon';

import type { TouchGraphStep } from './touchGraphLayout';
import { MinimalAddTile } from './MinimalAddTile';
import { stepStatusTone, TONE_CHIP_CLASS } from './touchStepStatus';

interface Props {
  blockId: string;
  /** The touch's steps — always at least one (the empty placeholder is handled upstream). */
  steps: TouchGraphStep[];
  /** The step currently open in the editor panel (its segment is highlighted), if any. */
  selectedStepId?: string | null;
  /** Open a specific step. Omit to render read-only (segments aren't clickable). */
  onSelectStep?: (stepId: string) => void;
  /** Add another step to THIS touch — the hover-revealed "+" tile to the right. Omit to hide it. */
  onAddStep?: () => void;
}

/**
 * A touch's steps rendered as ONE segmented control — the same visual language as the
 * Input2 `ButtonGroup`: a single rounded, bordered container with hairline dividers
 * between segments (no box-per-step). A single step renders the same way — a one-segment
 * group — so every compact touch reads consistently.
 *
 * The add-step "+" lives OUTSIDE the group, tucked behind its right edge; it slides out
 * and fades in when you hover the touch (driven by the parent `group/node` — the same
 * reveal the delete control uses). So the group stays clean at rest and the affordance
 * surfaces only when you reach for it.
 *
 * The segments keep their own tooltip / per-contact status tint / `data-status` /
 * selection highlight (used by the contact page), so this is a look change, not a
 * behavior change: clicking a segment opens that step; clicking "+" adds one.
 */
export function TouchStepGroup({ blockId, steps, selectedStepId = null, onSelectStep, onAddStep }: Props) {
  return (
    <div className="relative inline-flex">
      <div
        data-testid={`touch-step-group-${blockId}`}
        className={cn(
          // z-10 so the group sits ABOVE the tucked "+" and occludes it at rest.
          'relative z-10 inline-flex items-stretch overflow-hidden rounded-lg border border-lynch-200 bg-white shadow-sm',
          'dark:border-slate-700 dark:bg-slate-900',
        )}>
        {steps.map((step, i) => {
          const visual = stepStatusTone(step.contactStatus);
          return (
            <Fragment key={step.id}>
              {i > 0 && <div className="w-px shrink-0 self-stretch bg-lynch-200 dark:bg-slate-700" />}
              <Tooltip
                as="span"
                tooltip={visual?.tooltip}
                tooltipMaxWidth={260}
                data-testid={`touch-step-icon-${step.id}`}
                data-status={step.contactStatus?.state ?? undefined}
                onClick={
                  onSelectStep != null
                    ? (e: MouseEvent) => {
                        e.stopPropagation();
                        onSelectStep(step.id);
                      }
                    : undefined
                }
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center transition-colors',
                  onSelectStep != null && 'cursor-pointer',
                  visual != null ? TONE_CHIP_CLASS[visual.tone] : 'text-lynch-600 dark:text-lynch-300',
                  step.id === selectedStepId
                    ? 'bg-blue-100 text-blue-700 ring-1 ring-inset ring-blue-400 dark:bg-blue-500/20 dark:text-blue-200'
                    : onSelectStep != null && visual == null && 'hover:bg-lynch-100 dark:hover:bg-slate-800',
                )}>
                <StepIcon iconType={step.type} className="size-5" />
              </Tooltip>
            </Fragment>
          );
        })}
      </div>

      {/* Add-step "+" — parked BEHIND the group's right edge (z-0), hidden at rest; on
          hover of the touch (`group/node`) it slides out to the right and fades in. The
          same shared dashed-circle "add" look as the trailing / fork-arm add tiles. */}
      {onAddStep != null && (
        <MinimalAddTile
          testId={`touch-add-step-${blockId}`}
          tooltip="Add a step to this touch"
          onClick={(e) => {
            e.stopPropagation();
            onAddStep();
          }}
          className={cn(
            'absolute left-full top-1/2 z-0 ml-0.5 -translate-y-1/2',
            // Hidden + tucked left (behind the group) at rest; slides to its spot on the
            // right and fades in on hover. Mirrors the delete control's group/node reveal.
            'pointer-events-none -translate-x-5 opacity-0 transition-all duration-150 ease-out',
            'group-hover/node:pointer-events-auto group-hover/node:translate-x-0 group-hover/node:opacity-100',
          )}
        />
      )}
    </div>
  );
}
